import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mic, Music, Music2, Crown, Piano, Search, Star, MapPin, Phone, Mail, DollarSign } from 'lucide-react';

interface ArtistCategory {
  id: number;
  name: string;
  specialtyType: string;
  icon: string;
  color: string;
  createdAt: string;
}

interface Artist {
  id: number;
  name: string;
  stageName?: string;
  categoryId: number;
  description: string;
  bio: string;
  specialties: string[];
  experienceYears: number;
  contactPhone?: string;
  contactEmail?: string;
  location?: string;
  ratePerHour?: number;
  ratePerEvent?: number;
  availabilityStatus: string;
  isFeatured: boolean;
  isActive: boolean;
  profileImageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const iconMap = {
  Mic: Mic,
  Music: Music,
  Music2: Music2,
  Crown: Crown,
  Piano: Piano,
};

export default function ArtistsPortfolio() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  // Fetch artist categories
  const { data: categories = [] } = useQuery<ArtistCategory[]>({
    queryKey: ['/api/artists/categories'],
  });

  // Fetch artists
  const { data: artists = [], isLoading } = useQuery<Artist[]>({
    queryKey: ['/api/artists', { category: selectedCategory !== 'all' ? selectedCategory : undefined, featured: showFeaturedOnly }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (showFeaturedOnly) {
        params.append('featured', 'true');
      }
      
      const response = await fetch(`/api/artists?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch artists');
      }
      return response.json();
    },
  });

  // Filter artists based on search term
  const filteredArtists = artists.filter(artist =>
    artist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artist.stageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artist.specialties.some(specialty => specialty.toLowerCase().includes(searchTerm.toLowerCase())) ||
    artist.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group artists by category for display
  const categorizedArtists = categories.map(category => ({
    ...category,
    artists: filteredArtists.filter(artist => artist.categoryId === category.id)
  }));

  const ArtistCard = ({ artist }: { artist: Artist }) => {
    const category = categories.find(cat => cat.id === artist.categoryId);
    const IconComponent = category?.icon ? iconMap[category.icon as keyof typeof iconMap] : Mic;

    return (
      <Card className="hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
        <CardHeader className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${category?.color}20` }}>
                <IconComponent className="h-5 w-5" style={{ color: category?.color }} />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">{artist.name}</CardTitle>
                {artist.stageName && (
                  <p className="text-sm text-muted-foreground">"{artist.stageName}"</p>
                )}
              </div>
            </div>
            {artist.isFeatured && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                <Star className="h-3 w-3 mr-1 fill-current" />
                Featured
              </Badge>
            )}
          </div>
          <CardDescription className="text-sm line-clamp-2">
            {artist.description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Specialties */}
          <div className="flex flex-wrap gap-1">
            {artist.specialties.slice(0, 3).map((specialty, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {specialty}
              </Badge>
            ))}
            {artist.specialties.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{artist.specialties.length - 3} more
              </Badge>
            )}
          </div>

          {/* Experience and Location */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{artist.experienceYears} years experience</span>
            {artist.location && (
              <div className="flex items-center space-x-1">
                <MapPin className="h-3 w-3" />
                <span>{artist.location}</span>
              </div>
            )}
          </div>

          {/* Pricing */}
          {(artist.ratePerEvent || artist.ratePerHour) && (
            <div className="flex items-center space-x-2 text-sm">
              <DollarSign className="h-3 w-3 text-green-600" />
              <span>
                {artist.ratePerEvent && `₹${artist.ratePerEvent.toLocaleString()}/event`}
                {artist.ratePerEvent && artist.ratePerHour && ' • '}
                {artist.ratePerHour && `₹${artist.ratePerHour.toLocaleString()}/hour`}
              </span>
            </div>
          )}

          {/* Contact Actions */}
          <div className="flex space-x-2 pt-2">
            {artist.contactPhone && (
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1"
                onClick={() => window.open(`tel:${artist.contactPhone}`, '_self')}
              >
                <Phone className="h-3 w-3 mr-1" />
                Call
              </Button>
            )}
            {artist.contactEmail && (
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1"
                onClick={() => window.open(`mailto:${artist.contactEmail}`, '_self')}
              >
                <Mail className="h-3 w-3 mr-1" />
                Email
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
          Artists Portfolio
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Discover talented MCs, dancers, vocalists, and traditional performers for your special wedding day
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, specialty, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id.toString()}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showFeaturedOnly ? "default" : "outline"}
          onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
          className="w-full sm:w-auto"
        >
          <Star className="h-4 w-4 mr-2" />
          Featured Only
        </Button>
      </div>

      {/* Content */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-8">
          <TabsTrigger value="all" className="text-xs sm:text-sm">All</TabsTrigger>
          {categories.map((category) => {
            const IconComponent = iconMap[category.icon as keyof typeof iconMap] || Mic;
            return (
              <TabsTrigger key={category.id} value={category.id.toString()} className="text-xs sm:text-sm">
                <IconComponent className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">{category.name}</span>
                <span className="sm:hidden">{category.name.split('/')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="all">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="h-3 bg-muted rounded w-full"></div>
                      <div className="h-3 bg-muted rounded w-2/3"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              {categorizedArtists.map((category) => (
                category.artists.length > 0 && (
                  <div key={category.id}>
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${category.color}20` }}>
                        {iconMap[category.icon as keyof typeof iconMap] && 
                          iconMap[category.icon as keyof typeof iconMap]({ 
                            className: "h-5 w-5", 
                            style: { color: category.color } 
                          })
                        }
                      </div>
                      <h2 className="text-xl font-semibold">{category.name}</h2>
                      <Badge variant="secondary">{category.artists.length}</Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {category.artists.map((artist) => (
                        <ArtistCard key={artist.id} artist={artist} />
                      ))}
                    </div>
                  </div>
                )
              ))}
              {filteredArtists.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-muted-foreground text-lg">No artists found matching your criteria</div>
                  <p className="text-sm text-muted-foreground mt-2">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {categories.map((category) => (
          <TabsContent key={category.id} value={category.id.toString()}>
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-3 bg-muted rounded w-1/2"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="h-3 bg-muted rounded w-full"></div>
                        <div className="h-3 bg-muted rounded w-2/3"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArtists
                  .filter(artist => artist.categoryId === category.id)
                  .map((artist) => (
                    <ArtistCard key={artist.id} artist={artist} />
                  ))}
              </div>
            )}
            {filteredArtists.filter(artist => artist.categoryId === category.id).length === 0 && (
              <div className="text-center py-12">
                <div className="text-muted-foreground text-lg">No {category.name.toLowerCase()} found</div>
                <p className="text-sm text-muted-foreground mt-2">Try adjusting your search terms</p>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}